import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ethers } from "ethers";
import {
  getWeb3ProviderForChain,
  readProvider,
} from "../utils/wallet/activeSigner";
import { getCurrentChainConfig } from "../config/chains";
import useWallet from "./wallet/useWallet";
import { AuthContext } from "../contexts/AuthContext";
import { LocalStorageKeyEnum } from "../enums/localStorage";
import { IAuthInfo } from "../interfaces/AuthInfo";
import {
  listSubaccounts,
  createSubaccount as createSubaccountUtil,
  fundSubaccount as fundSubaccountUtil,
  defundSubaccount as defundSubaccountUtil,
  unlinkSubaccount as unlinkSubaccountUtil,
  registerSubaccountApiKey,
  removeSubaccountLocal,
  SubaccountView,
} from "../utils/subaccounts";
import { isValidDecimalInput, toTokenUnits } from "../utils/decimalString";

const BALANCE_ABI = ["function getBalance(address user, address currency) view returns (uint256)"];

/**
 * A typed USDC amount as base-unit integer text, or a message the trader can
 * act on. `parseUnits` threw ethers' raw "fractional component exceeds
 * decimals" for one digit too many.
 */
export const toSubaccountAmountUnits = (
  amountDisplay: string,
  decimals: number
): string => {
  const text = String(amountDisplay ?? "").trim();
  const units = isValidDecimalInput(text, decimals)
    ? toTokenUnits(text, decimals)
    : null;
  if (!units) {
    throw new Error(`Enter a valid amount with at most ${decimals} decimals`);
  }
  if (units.isZero()) {
    throw new Error("Enter an amount greater than 0");
  }
  return units.toString();
};

/**
 * Subaccount management for the connected (master) wallet. Exposes the list of the master's
 * subaccounts, their engine collateral balances, and gasless create / fund / defund / unlink actions.
 *
 * Trading AS a subaccount (switching the active trading identity) is a separate concern layered on
 * the AuthContext `account` seam; this hook covers creation and funding, which is what makes a
 * subaccount usable.
 */
export function useSubaccounts() {
  const config = getCurrentChainConfig();
  const chainId = config.chainId;
  const { account: master } = useWallet();
  const { authInfoList, enableTrading, setActiveSubaccount, activeSubaccount } = useContext(AuthContext);

  const perpEngine =
    config.contracts.perpetualDex?.perpRouter || config.contracts.perpetualDex?.orderHandler;
  const spotEngine = config.contracts.perpetualDex?.router;
  const usdc = config.contracts.perpetualDex?.usdc || config.contracts.erc20?.usdc;
  const relayerUrl = config.relayerUrl;
  const usdcDecimals = config.tokenDecimals?.usdc ?? 6;
  const supported = !!(
    config.supportsSubaccounts &&
    config.gasless &&
    relayerUrl &&
    perpEngine &&
    spotEngine &&
    usdc
  );

  const [version, setVersion] = useState(0); // bump to re-read localStorage
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [balances, setBalances] = useState<Record<string, string>>({}); // raw balance keyed by sub addr

  const subs: SubaccountView[] = useMemo(
    () => (master ? listSubaccounts(master, chainId) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [master, chainId, version]
  );

  // Async now, and chain-guarded: this hands a SIGNING provider to the
  // subaccount helpers, so it must refuse when the wallet is on another network
  // rather than let a transaction be signed for the wrong chain.
  const walletProvider = useCallback(
    () => getWeb3ProviderForChain(config),
    [config]
  );

  const refreshBalances = useCallback(async () => {
    if (!spotEngine || !usdc || subs.length === 0) {
      setBalances({});
      return;
    }
    try {
      // Balances are a read: the configured RPC, never the wallet.
      const provider = readProvider(config);
      const engine = new ethers.Contract(spotEngine, BALANCE_ABI, provider);
      const entries = await Promise.all(
        subs.map(async (s): Promise<[string, string | undefined]> => {
          try {
            const b = await engine.getBalance(s.address, usdc);
            return [s.address.toLowerCase(), b.toString()];
          } catch {
            // A failed read is NOT an empty subaccount. Storing "0" here made
            // the two indistinguishable, which is why the Withdraw button
            // could never be capped by the balance at all.
            return [s.address.toLowerCase(), undefined];
          }
        })
      );
      setBalances(
        Object.fromEntries(
          entries.filter((entry): entry is [string, string] => entry[1] !== undefined)
        )
      );
    } catch {
      /* best-effort */
    }
  }, [spotEngine, usdc, subs, config.rpcUrls]);

  useEffect(() => {
    refreshBalances();
  }, [refreshBalances]);

  const create = useCallback(
    async (label?: string) => {
      if (!master) throw new Error("Connect your wallet first");
      if (!supported) throw new Error("Subaccounts aren't available on this network");
      setError(null);
      setBusy(true);
      try {
        const address = await createSubaccountUtil({
          provider: await walletProvider(),
          master,
          chainId,
          perpEngine: perpEngine as string,
          relayerUrl: relayerUrl as string,
          label,
        });
        setVersion((v) => v + 1);
        return address;
      } catch (e: any) {
        setError(e?.message || "Failed to create subaccount");
        throw e;
      } finally {
        setBusy(false);
      }
    },
    [master, supported, chainId, perpEngine, relayerUrl, walletProvider]
  );

  const fund = useCallback(
    async (subAddress: string, amountDisplay: string) => {
      if (!master) throw new Error("Connect your wallet first");
      setError(null);
      setBusy(true);
      try {
        const amount = toSubaccountAmountUnits(amountDisplay, usdcDecimals);
        const tx = await fundSubaccountUtil({
          provider: await walletProvider(),
          master,
          chainId,
          spotEngine: spotEngine as string,
          relayerUrl: relayerUrl as string,
          currency: usdc as string,
          subAddress,
          amount,
        });
        await refreshBalances();
        return tx;
      } catch (e: any) {
        setError(e?.message || "Failed to fund subaccount");
        throw e;
      } finally {
        setBusy(false);
      }
    },
    [master, chainId, spotEngine, relayerUrl, usdc, usdcDecimals, walletProvider, refreshBalances]
  );

  const defund = useCallback(
    async (subAddress: string, amountDisplay: string) => {
      if (!master) throw new Error("Connect your wallet first");
      setError(null);
      setBusy(true);
      try {
        const amount = toSubaccountAmountUnits(amountDisplay, usdcDecimals);
        const tx = await defundSubaccountUtil({
          master,
          chainId,
          spotEngine: spotEngine as string,
          relayerUrl: relayerUrl as string,
          currency: usdc as string,
          subAddress,
          amount,
        });
        await refreshBalances();
        return tx;
      } catch (e: any) {
        setError(e?.message || "Failed to withdraw from subaccount");
        throw e;
      } finally {
        setBusy(false);
      }
    },
    [master, chainId, spotEngine, relayerUrl, usdc, usdcDecimals, refreshBalances]
  );

  const unlink = useCallback(
    async (subAddress: string) => {
      if (!master) throw new Error("Connect your wallet first");
      setError(null);
      setBusy(true);
      try {
        await unlinkSubaccountUtil({
          provider: await walletProvider(),
          master,
          chainId,
          perpEngine: perpEngine as string,
          subAddress,
        });
        setVersion((v) => v + 1);
      } catch (e: any) {
        setError(e?.message || "Failed to unlink subaccount");
        throw e;
      } finally {
        setBusy(false);
      }
    },
    [master, chainId, perpEngine, walletProvider]
  );

  const forget = useCallback(
    (subAddress: string) => {
      if (!master) return;
      removeSubaccountLocal(master, chainId, subAddress);
      setVersion((v) => v + 1);
    },
    [master, chainId]
  );

  // Switch the active trading identity to a subaccount. Registers the sub's own API key on first use
  // (so positions/orders/balances follow it) and flips AuthContext.account to the sub. Perp-only —
  // spot has no on-chain subaccount authorization. Fail-safe: on any error we stay on the master.
  const tradeAs = useCallback(
    async (subAddress: string) => {
      if (!master) throw new Error("Connect your wallet first");
      setError(null);
      setBusy(true);
      try {
        const already = Object.entries(authInfoList || {}).some(
          ([k, v]: [string, any]) =>
            k.toLowerCase() === subAddress.toLowerCase() ||
            v?.account?.toLowerCase() === subAddress.toLowerCase()
        );
        if (!already) {
          const referralCode = window.localStorage.getItem(LocalStorageKeyEnum.REFERRAL_CODE) || undefined;
          const authInfo = await registerSubaccountApiKey({ master, chainId, subAddress, referralCode });
          enableTrading(authInfo as unknown as IAuthInfo, true);
        }
        setActiveSubaccount(subAddress);
      } catch (e: any) {
        setError(e?.message || "Failed to switch to subaccount");
        throw e;
      } finally {
        setBusy(false);
      }
    },
    [master, chainId, authInfoList, enableTrading, setActiveSubaccount]
  );

  const tradeAsMaster = useCallback(() => setActiveSubaccount(undefined), [setActiveSubaccount]);

  return {
    supported,
    master,
    subs,
    balances,
    usdcDecimals,
    busy,
    error,
    create,
    fund,
    defund,
    unlink,
    forget,
    refresh: refreshBalances,
    tradeAs,
    tradeAsMaster,
    activeSubaccount,
  };
}
